/* The compliance checklist (gate 26). Every colour is a token (ADR-0007, enforced by stylelint).
   Status is a WORD on every item, with a glyph; colour only reinforces it (design rule 8).

   Sections sit in a responsive grid of columns rather than one tall list: six short lists read
   faster side by side than stacked, and the panel stops being the longest thing on the screen. */

.tally {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
  font-size: var(--t-min);
  color: var(--ink-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
  width: fit-content;
  max-width: 100%;
}

.tally li {
  padding: 5px 14px;
  border-left: 1px solid var(--line);
}

.tally li:first-child {
  border-left: 0;
}

.tally strong {
  margin-right: 3px;
  font-size: var(--t-dense);
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--ink);
}

.sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 430px), 1fr));
  gap: 18px 28px;
  align-items: start;
}

.section {
  min-width: 0;
}

.sectionHead {
  margin: 0 0 2px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--line-2);
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.items {
  margin: 0;
  padding: 0;
  list-style: none;
}

.item {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid var(--line);
}

.item:first-child {
  border-top: 0;
}

/* The status: glyph and word in one outlined pill. */
.status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  place-self: start start;
  padding: 1px 8px 1px 6px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--line-2);
  background: var(--panel-2);
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
}

.statusMet {
  background: var(--ok-w);
  border-color: var(--ok-line);
  color: var(--ok);
}

.statusOpen {
  background: var(--crit-w);
  border-color: var(--crit-line);
  color: var(--crit);
}

.statusUnchecked {
  background: var(--warn-w);
  border-color: var(--warn-line);
  color: var(--warn);
}

.statusPerson {
  background: var(--info-w);
  border-color: var(--info-line);
  color: var(--info);
}

.body {
  min-width: 0;
  display: grid;
  gap: 3px;
  font-size: var(--t-dense);
  overflow-wrap: anywhere;
}

.title {
  font-weight: 600;
  color: var(--ink);
}

.code {
  margin-left: 8px;
  font-weight: 400;
  color: var(--muted);
}

/* The row's own words, set apart so a reader can see which sentences are the regulation's. */
.text {
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
  max-width: 72ch;
}

.note {
  color: var(--ink-2);
  font-size: var(--t-min);
  line-height: 1.5;
  max-width: 72ch;
}

.noteKind {
  margin-right: 6px;
  font-weight: 600;
  color: var(--muted);
}

.cite {
  font-size: var(--t-min);
  color: var(--muted);
}

.figure {
  font-variant-numeric: lining-nums tabular-nums;
}

/* The regime's standing, above the counts: whether this is law. Warm, because a proposed rule is
   the thing a reader must not miss, but a rule beside the text and not a filled box. */
.inForce,
.qualifier {
  margin: 0 0 12px;
  padding: 1px 0 1px 10px;
  border-left: 2px solid var(--warn);
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 92ch;
}

.qualifier {
  display: block;
  margin: 2px 0;
}

.qualifierTitle {
  display: block;
  font-weight: 600;
  color: var(--warn);
}

@media (max-width: 700px) {
  .item {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }
}
